import { useState, type ReactNode } from "react";
import { useAction } from "../../api/mutations";
import { Card, Note } from "../../components/Card";
import { QueryState } from "../../components/States";
import type { ReadResult } from "../../api/queries";
import { actionPayload, type ActionField } from "../../lib/actionPayload";

export function ScreenState<T>({
  query,
  what,
  children,
}: {
  query: ReadResult<T>;
  what: string;
  children: (data: T) => ReactNode;
}): ReactNode {
  return (
    <QueryState status={query.status} error={query.error} what={what} refetch={() => void query.refetch()}>
      {query.status === "success" ? children(query.data as T) : null}
    </QueryState>
  );
}
export function ActionForm({
  path,
  fields,
  title = "操作",
  extra,
  note,
}: {
  path: string;
  fields: ActionField[];
  title?: string;
  /** 表单之外随请求带上的固定字段（如当前交易日）；与表单同名时以这里为准。 */
  extra?: Record<string, string | boolean>;
  /** 表单上方的一句说明。 */
  note?: ReactNode;
}): ReactNode {
  const action = useAction(path);
  const [values, setValues] = useState<Record<string, string | boolean>>({});
  const missing = fields.some(
    (f) => f.required !== false && f.type !== "checkbox" && !String(values[f.name] ?? "").trim(),
  );
  function update(name: string, value: string | boolean): void {
    setValues((v) => ({ ...v, [name]: value }));
  }
  return (
    <Card title={title} sub="提交后由服务端作最终判定">
      {note ? <Note>{note}</Note> : null}
      <div className="fieldrow">
        {fields.map((f) => (
          <label className={`f ${f.type === "checkbox" ? "check" : ""}`} key={f.name}>
            <span>{f.label}</span>
            {f.type === "checkbox" ? (
              <input
                type="checkbox"
                checked={values[f.name] === true}
                onChange={(e) => update(f.name, e.target.checked)}
              />
            ) : f.type === "select" ? (
              <select value={String(values[f.name] ?? "")} onChange={(e) => update(f.name, e.target.value)}>
                <option value="">请选择</option>
                {f.options?.map((option) => (
                  <option key={option} value={option}>
                    {option}
                  </option>
                ))}
              </select>
            ) : (
              <input
                type={f.type ?? "text"}
                value={String(values[f.name] ?? "")}
                onChange={(e) => update(f.name, e.target.value)}
              />
            )}
          </label>
        ))}
      </div>
      <button
        className="btn primary"
        disabled={missing || action.isPending}
        onClick={() => action.mutate({ ...actionPayload(fields, values), ...extra })}
      >
        {action.isPending ? "提交中…" : "提交"}
      </button>
      {missing ? <Note tone="warn">请填写必填字段。</Note> : null}
      {action.isError ? (
        <Note tone="bad">{action.error instanceof Error ? action.error.message : String(action.error)}</Note>
      ) : null}
      {action.isSuccess ? <Note>服务端已受理本次操作。</Note> : null}
    </Card>
  );
}
export function RawDetails({ value }: { value: object }): ReactNode {
  return (
    <details>
      <summary>原始 JSON</summary>
      <pre className="code">{JSON.stringify(value, null, 2)}</pre>
    </details>
  );
}
